import { Box, Button, Flex, FormControlProps, Link, Spinner, Text, useFormControlContext } from '@chakra-ui/react'
import { ReactNode, useCallback } from 'react'

import { FormField } from '../FormField'
import { Icon } from '../Icon'
import FilePreviewList, { TFilePreview } from './FilePreviewList'
import { FILE_TYPES, TFileAccept } from './fileTypes'
import { useFilePicker } from './useFilePicker'

export interface IFileInputProps extends Omit<FormControlProps, 'onChange' | 'label' | 'children'> {
  /** O que o campo aceita, por intenção. A lista de formatos e a dica vêm do registro. */
  accept: TFileAccept
  value: File | null
  onChange: (file: File | null) => void
  /** Tamanho máximo em MB. Espelhe o `max:` da regra do endpoint. */
  maxSizeMB?: number
  label?: ReactNode
  isRequired?: boolean
  isDisabled?: boolean
  error?: string | string[] | null
  /** Texto de apoio próprio do campo. A linha de formatos aceitos é automática. */
  helperText?: ReactNode
  /** Forma da prévia do arquivo escolhido. */
  preview?: TFilePreview
  /** URL do arquivo já guardado (modo edição), exibida enquanto nada novo é escolhido. */
  existingUrl?: string | null
  /** Rótulo do botão quando não há nada escolhido. */
  placeholder?: string
}

interface IPickButtonProps {
  children: ReactNode
  isDisabled?: boolean
  isLoading: boolean
  onClick: () => void
}

/**
 * O botão que abre o seletor, num componente próprio pelo mesmo motivo do `DropArea`: é aqui
 * dentro que o `useFormControlContext` existe. Sem o `id`, o `htmlFor` do `<FormLabel>` apontava
 * para um elemento que não existia, e clicar no rótulo não fazia nada.
 */
function PickButton({ children, isDisabled, isLoading, onClick }: IPickButtonProps) {
  const field = useFormControlContext()

  return (
    <Button
      id={field?.id}
      aria-describedby={field?.hasFeedbackText ? field.feedbackId : undefined}
      aria-invalid={field?.isInvalid || undefined}
      aria-required={field?.isRequired || undefined}
      size="sm"
      variant="outline"
      leftIcon={<Icon name="lni lni-upload" />}
      onClick={onClick}
      isDisabled={isDisabled}
      isLoading={isLoading}
    >
      {children}
    </Button>
  )
}

/**
 * Campo de um arquivo só: botão, prévia do que foi escolhido e a linha de formatos aceitos.
 *
 * Substitui as quatro formas em que o painel pedia um arquivo — o `<Input type="file" p={1}>`
 * cru (9 lugares, um deles sem o padding, com altura diferente dos campos vizinhos), o input
 * escondido com `<label>` e `<Button as="span">`, o input escondido com um botão que o clica, e o
 * `<Input display="none">` com prévia desenhada à mão. Nenhuma delas validava tipo e tamanho do
 * mesmo jeito: uma recusava em silêncio, outra recusava tudo quando um arquivo era inválido, e
 * três não checavam tamanho nenhum apesar de anunciarem um limite na tela.
 *
 * O campo inteiro passa pelo `FormField`, como `ImageCropInput` e `VideoFileInput` — rótulo,
 * obrigatoriedade e erro numa convenção só. Não embrulhe num `FormControl` de novo.
 */
export default function FileInput({
  accept,
  value,
  onChange,
  maxSizeMB,
  label,
  isRequired,
  isDisabled,
  error,
  helperText,
  preview = 'list',
  existingUrl,
  placeholder,
  ...rest
}: IFileInputProps) {
  const onPick = useCallback((files: File[]) => onChange(files[0] ?? null), [onChange])
  const { open, inputProps, isProcessing } = useFilePicker({ accept, maxSizeMB, onPick, isDisabled })

  const hasStored = Boolean(existingUrl) && !value
  const formats = FILE_TYPES[accept].label
  const hint = maxSizeMB ? `Formatos aceitos: ${formats}. Até ${maxSizeMB}MB.` : `Formatos aceitos: ${formats}.`

  return (
    <FormField label={label} error={error} isRequired={isRequired} isDisabled={isDisabled} {...rest}>
      {value && (
        <FilePreviewList files={[value]} variant={preview} isDisabled={isDisabled} onRemove={() => onChange(null)} />
      )}

      {hasStored && (
        <Text variant="caption">
          Arquivo atual:{' '}
          <Link href={existingUrl as string} isExternal color="magenta.600" textDecoration="underline">
            abrir
          </Link>
        </Text>
      )}

      <input {...inputProps} />

      <Flex align="center" gap={2} mt={value || hasStored ? 2 : 0}>
        <PickButton onClick={open} isDisabled={isDisabled} isLoading={isProcessing}>
          {value || hasStored ? 'Trocar arquivo' : (placeholder ?? 'Selecionar arquivo')}
        </PickButton>
        {/* O spinner do botão some quando ele está desabilitado; esta é a pista de que a imagem
            grande está sendo recomprimida, que pode levar alguns segundos. */}
        {isProcessing && isDisabled && <Spinner size="sm" color="gray.500" />}
      </Flex>

      <Text variant="caption" mt={1}>
        {hint}
      </Text>

      {/* `Box` e não `Text`: o `helperText` é um `ReactNode` e pode trazer bloco — a tela de
          comprovantes passa um `<Flex>` com o spinner de leitura. Um `<div>` dentro do `<p>` que o
          `<Text>` renderiza é HTML inválido: dá aviso de `validateDOMNesting` e o browser fecha o
          parágrafo antes da hora, quebrando o layout. */}
      {helperText && (
        <Box fontSize="xs" color="gray.500" mt={1}>
          {helperText}
        </Box>
      )}
    </FormField>
  )
}
